// Interface every client-side tool implementation follows. Each tool encapsulates
// its own execution, display label, and execution-strategy category so that the
// generic runtime (useExecuteDomTools) can dispatch without knowing tool internals.

import { ToolUseBlock } from './toolBlocks'
import { PAGE_NOT_LOADED } from '../../../lib/context/PageContext'

export type ToolCategory = 'navigate' | 'mutation'

export interface ToolExecutionResult {
  /** Whether the operation succeeded. Drives step-status (done vs error) in the UI. */
  success: boolean
  /** String placed in the ToolResultBlock content and sent back to the LLM. */
  content: string
}

export function success(): ToolExecutionResult {
  return { success: true, content: JSON.stringify({ success: true }) }
}

export function failure(error: string): ToolExecutionResult {
  return { success: false, content: JSON.stringify({ success: false, error }) }
}

/** The route push always succeeds, so whether a student page loaded is only known
 *  from the page context the assistant receives next. */
export function studentPageRequested(pathname: string): ToolExecutionResult {
  return {
    success: true,
    content: `Navigation requested. Current URL: ${pathname}. If the next page context says "${PAGE_NOT_LOADED}", tell the advisor the page did not open.`
  }
}

export interface ExecutionContext {
  /** Performs a client-side route change. */
  navigate: (path: string) => void
  /** Returns the current URL pathname. Meaningful after navigation has settled. */
  getPathname: () => string
}

export interface ClientTool {
  /** Tool identifier matching the server's tool definition name. */
  name: string
  /**
   * Execution strategy. Navigate tools run one-at-a-time (first wins, rest
   * skipped); mutation tools run sequentially in the order received.
   */
  category: ToolCategory
  /** Executes the tool and returns a result the runtime can forward to the LLM. */
  execute: (input: ToolUseBlock['input'], ctx: ExecutionContext) => Promise<ToolExecutionResult>
  /** Returns a human-readable step label for this specific invocation. */
  getStepLabel: (input: ToolUseBlock['input']) => string
}
